import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ValidateEmailRequest {
  email: string;
}

export interface ValidateEmailResponse {
  payload: boolean;
}

export interface ValidateCommercialNumberRequest {
  commercialRegister: string;
}

export interface ValidateCommercialNumberResponse {
  payload: boolean;
}

export interface ValidateTaxNumberRequest {
  taxNumber: string;
}

export interface ValidateTaxNumberResponse {
  payload: boolean;
}

export interface SuggestUsernameRequest {
  firstName: string;
  lastName: string;
}

export interface SuggestUsernameResponse {
  payload: string[];
}

export interface ValidateUsernameRequest {
  username: string;
}

export interface ValidateUsernameResponse {
  payload: boolean;
}

export interface AddSellerPayload {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileId: string;
  userName: string;
  phone?: {
    phoneCode: string;
    phoneNumber: string;
  };
  address?: {
    id: string;
    street: string | null;
  };
}

export interface AddSellerResponse {
  payload: AddSellerPayload;
  code?: number;
  error?: string | null;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const addSellerService = createApi({
  reducerPath: 'addSellerService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Sellers'],
  endpoints: (builder) => ({
    validateEmail: builder.mutation<ValidateEmailResponse, ValidateEmailRequest>(
      {
        query: (body) => ({
          url: '/user-service/v1/users/validate-email-by-admin',
          method: 'POST',
          body,
        }),
      }
    ),
    validateCommercialNumber: builder.mutation<
      ValidateCommercialNumberResponse,
      ValidateCommercialNumberRequest
    >({
      query: (body) => ({
        url: '/profile-service/v1/profiles/validate-commercial-register',
        method: 'POST',
        body,
      }),
    }),
    validateTaxNumber: builder.mutation<
      ValidateTaxNumberResponse,
      ValidateTaxNumberRequest
    >({
      query: (body) => ({
        url: '/profile-service/v1/profiles/validate-tax-number',
        method: 'POST',
        body,
      }),
    }),
    suggestUsernames: builder.mutation<
      SuggestUsernameResponse,
      SuggestUsernameRequest
    >({
      query: (body) => ({
        url: '/user-service/v1/users/suggest-username',
        method: 'POST',
        body,
      }),
    }),
    validateUsername: builder.mutation<
      ValidateUsernameResponse,
      ValidateUsernameRequest
    >({
      query: (body) => ({
        url: '/user-service/v1/users/validate-username',
        method: 'POST',
        body,
      }),
    }),
    addSeller: builder.mutation<AddSellerResponse, FormData>({
      query: (body) => ({
        url: '/profile-service/v1/business-seller/create/by-admin',
        method: 'POST',
        body,
      }),
    }),
    getSellers: builder.query<any, { page: number; limit: number; search?: string }>({
      query: (params) => ({
        url: '/profile-service/v1/operator/seller',
        params: {
          page: params.page,
          size: params.limit,
          search: params.search,
        },
      }),
      providesTags: ['Sellers'],
    }),
    deleteSeller: builder.mutation<any, string>({
      query: (id) => ({
        url: `/profile-service/v1/users/admin/delete-profile/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['Sellers']),
    }),
    getSliders: builder.query<any, string>({
      query: (sellerProfileId) => ({
        url: `/profile-service/v1/business-seller/${sellerProfileId}/slider/by-admin`,
        method: 'GET',
      }),
    }),
    addSliderImages: builder.mutation<any, { sellerProfileId: string; data: FormData }>({
      query: ({ sellerProfileId, data }) => ({
        url: `/profile-service/v1/business-seller/${sellerProfileId}/slider-images/by-admin`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useValidateEmailMutation,
  useValidateCommercialNumberMutation,
  useValidateTaxNumberMutation,
  useSuggestUsernamesMutation,
  useValidateUsernameMutation,
  useAddSellerMutation,
  useGetSellersQuery,
  useDeleteSellerMutation,
  useGetSlidersQuery,
  useAddSliderImagesMutation,
} = addSellerService;
